import React, { useState } from 'react'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  ArrowDown,
  ArrowUp,
  GitBranch,
  Link2,
  Lock,
  PencilLine,
} from 'lucide-react'
import type { RatePlanInfo, RoomsSetupRoomType } from '@/types/shared'

interface BoundsState {
  min: string
  max: string
}

export interface LinkState {
  parentRoomTypeId: number | null
  offsetType: 'percentage' | 'absolute'
  offsetValue: string
}

// What the modal hands back to the page when the user presses "Apply".
// These are staged into the page's pending edits — nothing is persisted
// to the server until the page's own Review & Save / Compute step runs.
export interface RoomDraft {
  rates: Record<string, string>
  bounds: BoundsState
  link: LinkState
}

/** A room this one may follow, by its name on Rooms Setup (the building's list). */
export interface ParentOption {
  id: number
  name: string
}

interface FieldErrors {
  parent?: string
  offset?: string
  min?: string
  max?: string
  rates?: Record<string, string>
}

interface RoomTypeModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  room: RoomsSetupRoomType
  currencySymbol: string
  /** Initial snapshot — copied into local draft state on mount. */
  rates: Record<string, string>
  /** Initial snapshot — copied into local draft state on mount. */
  bounds: BoundsState
  /** Initial snapshot — copied into local draft state on mount. */
  link: LinkState
  leadRatePlanId: number | null
  /**
   * Eligible parent rooms, by name, in the page's order. At a combined inventory the list is the
   * building's rooms, so a ladder may cross listings without the picker naming them: a name two rooms
   * share already carries its listing in brackets (App::BuildingRooms). Eligibility is unchanged and
   * re-validated by the backend.
   */
  parentOptions: ParentOption[]
  derivedPlanCount: number
  /** Commit the staged draft to the page's pending edits. */
  onApply: (roomId: number, draft: RoomDraft) => void
  /** Lead plan + derived rates stay live (own nested flow), not drafted. */
  onLeadPlanChange: (roomTypeId: number, planId: number) => void
  onOpenDerivedRates: (roomTypeId: number) => void
}

function isValidNumber(v: string) {
  return v.trim() !== '' && Number.isFinite(Number(v))
}

export default function RoomTypeModal({
  open,
  onOpenChange,
  room: rt,
  currencySymbol: sym,
  rates,
  bounds,
  link,
  leadRatePlanId,
  parentOptions,
  derivedPlanCount,
  onApply,
  onLeadPlanChange,
  onOpenDerivedRates,
}: RoomTypeModalProps) {
  const isPerRoom = rt.priceStrategy === 'per_room'
  const occCount = isPerRoom ? 1 : rt.maxOccupancy
  // D4: per-occupancy rooms cannot be linked in v1 (per-room pricing only).
  const canLink = isPerRoom

  // --- Draft state: a fresh snapshot of the incoming props. The page keys
  // this component by room id and unmounts it on close, so every open is a
  // clean mount and these lazy initializers always read current values.
  // Typing here mutates local state only — the page never re-renders until
  // the user presses "Apply".
  const [draftRates, setDraftRates] = useState<Record<string, string>>(() => ({ ...rates }))
  const [draftBounds, setDraftBounds] = useState<BoundsState>(() => ({ ...bounds }))
  const [draftLink, setDraftLink] = useState<LinkState>(() => ({ ...link }))
  const [linkEnabled, setLinkEnabled] = useState(() => link.parentRoomTypeId != null)
  const [errors, setErrors] = useState<FieldErrors>({})

  // The staged parent, read back in the trigger. Real names are long ("Standard-Studio-Ensuite-Basement
  // level LG"), so the trigger wraps the name rather than clipping it inside the 480px dialog.
  const selectedParent = parentOptions.find((p) => p.id === draftLink.parentRoomTypeId)

  function setRate(occ: string, value: string) {
    setDraftRates((prev) => ({ ...prev, [occ]: value }))
    if (errors.rates?.[occ]) {
      setErrors((prev) => {
        const next = { ...(prev.rates || {}) }
        delete next[occ]
        return { ...prev, rates: next }
      })
    }
  }

  function setBound(field: 'min' | 'max', value: string) {
    setDraftBounds((prev) => ({ ...prev, [field]: value }))
    if (errors.min || errors.max) setErrors((prev) => ({ ...prev, min: undefined, max: undefined }))
  }

  function patchLink(patch: Partial<LinkState>) {
    setDraftLink((prev) => ({ ...prev, ...patch }))
    if (patch.parentRoomTypeId !== undefined && errors.parent)
      setErrors((prev) => ({ ...prev, parent: undefined }))
    if (patch.offsetValue !== undefined && errors.offset)
      setErrors((prev) => ({ ...prev, offset: undefined }))
  }

  function toggleLinked(next: boolean) {
    setLinkEnabled(next)
    setErrors({})
    if (next) {
      // No auto-select — the hotelier picks the parent deliberately.
      setDraftLink((prev) => ({
        parentRoomTypeId: null,
        offsetType: prev.offsetType || 'percentage',
        offsetValue: prev.offsetValue || '',
      }))
    }
  }

  function validate(): FieldErrors {
    const next: FieldErrors = {}

    if (linkEnabled) {
      if (draftLink.parentRoomTypeId == null) next.parent = 'Select a parent room'
      if (!isValidNumber(draftLink.offsetValue)) next.offset = 'Enter a price difference'
    } else {
      const rateErrors: Record<string, string> = {}
      for (let i = 0; i < occCount; i++) {
        const key = String(i + 1)
        const v = draftRates[key] ?? ''
        if (v.trim() !== '' && (!Number.isFinite(Number(v)) || Number(v) < 0))
          rateErrors[key] = 'Enter a valid rate'
      }
      if (Object.keys(rateErrors).length > 0) next.rates = rateErrors
    }

    const min = draftBounds.min.trim()
    const max = draftBounds.max.trim()
    if (min !== '' && (!Number.isFinite(Number(min)) || Number(min) < 0))
      next.min = 'Invalid floor'
    if (max !== '' && (!Number.isFinite(Number(max)) || Number(max) < 0))
      next.max = 'Invalid ceiling'
    if (!next.min && !next.max && min !== '' && max !== '' && Number(min) > Number(max))
      next.max = 'Ceiling must be ≥ floor'

    return next
  }

  function handleApply() {
    const found = validate()
    if (Object.keys(found).length > 0) {
      setErrors(found)
      return
    }
    onApply(rt.id, {
      rates: { ...draftRates },
      bounds: { ...draftBounds },
      link: linkEnabled
        ? { ...draftLink }
        : { parentRoomTypeId: null, offsetType: draftLink.offsetType, offsetValue: '' },
    })
    onOpenChange(false)
  }

  const FloorCeiling = (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-amber-600">
          <ArrowDown className="h-3 w-3" />
          Floor
        </Label>
        <div className="relative">
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
            {sym}
          </span>
          <Input
            type="number"
            step="0.01"
            min="0"
            className={cn(
              'rounded-lg border-amber-200 bg-amber-50/30 pl-7 tabular-nums focus:border-amber-400 focus:ring-amber-400/20',
              errors.min && 'border-rose-300 focus:border-rose-400 focus:ring-rose-400/20'
            )}
            placeholder="No floor"
            value={draftBounds.min}
            onChange={(e) => setBound('min', e.target.value)}
          />
        </div>
        {errors.min ? (
          <p className="text-[10px] font-medium text-rose-600">{errors.min}</p>
        ) : (
          <p className="text-[10px] text-muted-foreground">Rates won't drop below this</p>
        )}
      </div>
      <div className="space-y-1.5">
        <Label className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-rose-600">
          <ArrowUp className="h-3 w-3" />
          Ceiling
        </Label>
        <div className="relative">
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
            {sym}
          </span>
          <Input
            type="number"
            step="0.01"
            min="0"
            className={cn(
              'rounded-lg border-rose-200 bg-rose-50/30 pl-7 tabular-nums focus:border-rose-400 focus:ring-rose-400/20',
              errors.max && 'border-rose-300 focus:border-rose-400 focus:ring-rose-400/20'
            )}
            placeholder="No limit"
            value={draftBounds.max}
            onChange={(e) => setBound('max', e.target.value)}
          />
        </div>
        {errors.max ? (
          <p className="text-[10px] font-medium text-rose-600">{errors.max}</p>
        ) : (
          <p className="text-[10px] text-muted-foreground">Rates won't exceed this</p>
        )}
      </div>
    </div>
  )

  const LeadPlanRow = rt.ratePlans && rt.ratePlans.length > 0 && (
    <div className="space-y-2 border-t border-border/40 pt-4">
      <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Lead Rate Plan
      </Label>
      <div className="flex items-center gap-3">
        <Select
          value={leadRatePlanId != null ? String(leadRatePlanId) : undefined}
          onValueChange={(val) => onLeadPlanChange(rt.id, parseInt(val, 10))}
        >
          <SelectTrigger className="h-9 flex-1 text-sm">
            <SelectValue placeholder="Select lead plan" />
          </SelectTrigger>
          <SelectContent>
            {(rt.ratePlans as RatePlanInfo[]).map((rp) => (
              <SelectItem key={rp.id} value={String(rp.id)}>
                {rp.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {leadRatePlanId != null && rt.ratePlans.length > 1 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="shrink-0 gap-1.5 text-xs text-primary hover:text-primary/80"
            onClick={() => onOpenDerivedRates(rt.id)}
          >
            <GitBranch className="h-3 w-3" />
            Derived Rates
            {derivedPlanCount > 0 && (
              <Badge
                variant="secondary"
                className="ml-0.5 border-transparent bg-primary/10 px-1.5 py-0 text-[10px] font-semibold text-primary"
              >
                {derivedPlanCount}
              </Badge>
            )}
          </Button>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground">
        Lead plan changes apply immediately to your staged edits.
      </p>
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {rt.name}
            <span
              className={cn(
                'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wide',
                isPerRoom
                  ? 'bg-blue-50 text-blue-600 ring-1 ring-inset ring-blue-500/20'
                  : 'bg-violet-50 text-violet-600 ring-1 ring-inset ring-violet-500/20'
              )}
            >
              {isPerRoom ? 'Per Room' : 'Per Occupancy'}
            </span>
          </DialogTitle>
          <DialogDescription className="flex items-center gap-1.5 text-xs">
            <Lock className="h-3 w-3" />
            {rt.totalUnits} {rt.totalUnits === 1 ? 'unit' : 'units'} · Max occupancy{' '}
            {rt.maxOccupancy} · Synced from your PMS
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {/* Link toggle */}
          <div
            className={cn(
              'flex items-start justify-between gap-3 rounded-lg border p-3',
              linkEnabled ? 'border-primary/30 bg-primary/5' : 'border-border/60 bg-muted/30'
            )}
          >
            <div className="space-y-0.5">
              <Label className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                <Link2 className="h-3.5 w-3.5 text-primary" />
                Link to a parent room
              </Label>
              <p className="text-xs text-muted-foreground">
                {canLink
                  ? 'Track another room and price by a fixed difference.'
                  : 'Only per-room rooms can be linked in this version.'}
              </p>
            </div>
            <Switch
              checked={linkEnabled}
              disabled={!canLink || parentOptions.length === 0}
              onCheckedChange={toggleLinked}
            />
          </div>

          {linkEnabled ? (
            <div className="space-y-4">
              {/* Parent select */}
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Parent Room
                </Label>
                <Select
                  value={
                    draftLink.parentRoomTypeId != null
                      ? String(draftLink.parentRoomTypeId)
                      : undefined
                  }
                  onValueChange={(val) => patchLink({ parentRoomTypeId: parseInt(val, 10) })}
                >
                  {/* The trigger wraps the room name instead of clipping it to one line, so a long
                      name reads in full inside the 480px dialog. `[&>span:first-child]` outranks the
                      base `[&>span]:line-clamp-1` on specificity, so the wrap holds whatever order
                      the classes land in. */}
                  <SelectTrigger
                    className={cn(
                      'h-auto min-h-9 items-start py-1.5 text-left text-sm [&>span:first-child]:line-clamp-none [&>svg]:mt-0.5 [&>svg]:shrink-0',
                      errors.parent && 'border-rose-300'
                    )}
                  >
                    {selectedParent ? (
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="break-words leading-snug text-foreground">
                          {selectedParent.name}
                        </span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Select parent room</span>
                    )}
                  </SelectTrigger>
                  {/* Pinned to the trigger's width so the panel can never push past the dialog;
                      names wrap inside it instead of widening it. */}
                  <SelectContent className="max-h-[300px] w-[var(--radix-select-trigger-width)]">
                    <SelectGroup>
                      {parentOptions.map((p) => (
                        <SelectItem
                          key={p.id}
                          value={String(p.id)}
                          className="whitespace-normal break-words py-1.5 leading-snug"
                        >
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {errors.parent && (
                  <p className="text-[10px] font-medium text-rose-600">{errors.parent}</p>
                )}
              </div>

              {/* Offset */}
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Price Difference
                </Label>
                <div className="flex items-center gap-2">
                  <div className="flex overflow-hidden rounded-lg border border-border/60">
                    <button
                      type="button"
                      className={cn(
                        'px-3 py-2 text-sm font-medium transition-colors',
                        draftLink.offsetType === 'percentage'
                          ? 'bg-primary text-white'
                          : 'bg-white text-muted-foreground hover:bg-muted'
                      )}
                      onClick={() => patchLink({ offsetType: 'percentage' })}
                    >
                      %
                    </button>
                    <button
                      type="button"
                      className={cn(
                        'px-3 py-2 text-sm font-medium transition-colors',
                        draftLink.offsetType === 'absolute'
                          ? 'bg-primary text-white'
                          : 'bg-white text-muted-foreground hover:bg-muted'
                      )}
                      onClick={() => patchLink({ offsetType: 'absolute' })}
                    >
                      {sym}
                    </button>
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    className={cn(
                      'flex-1 rounded-lg tabular-nums',
                      errors.offset && 'border-rose-300 focus:border-rose-400 focus:ring-rose-400/20'
                    )}
                    placeholder="e.g. -10 or 20"
                    value={draftLink.offsetValue}
                    onChange={(e) => patchLink({ offsetValue: e.target.value })}
                  />
                </div>
                {errors.offset ? (
                  <p className="text-[10px] font-medium text-rose-600">{errors.offset}</p>
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    This room = parent's price{' '}
                    {draftLink.offsetType === 'percentage' ? 'adjusted by' : 'plus'}{' '}
                    <span className="font-medium tabular-nums text-foreground">
                      {draftLink.offsetValue || '0'}
                      {draftLink.offsetType === 'percentage' ? '%' : ` ${sym}`}
                    </span>
                    . Negative values price it below the parent.
                  </p>
                )}
              </div>

              {FloorCeiling}
              {LeadPlanRow}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Base rate(s) — same field treatment as Floor / Ceiling */}
              <div
                className={cn('grid gap-4', occCount === 1 ? 'grid-cols-1' : 'grid-cols-2')}
              >
                {Array.from({ length: occCount }, (_, i) => {
                  const occ = i + 1
                  const key = String(occ)
                  const rateError = errors.rates?.[key]
                  return (
                    <div key={occ} className="space-y-1.5">
                      <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {isPerRoom
                          ? 'Base Rate'
                          : `Base Rate · ${occ} ${occ === 1 ? 'guest' : 'guests'}`}
                      </Label>
                      <div className="relative">
                        <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                          {sym}
                        </span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          className={cn(
                            'rounded-lg border-border/60 pl-7 tabular-nums',
                            rateError &&
                              'border-rose-300 focus:border-rose-400 focus:ring-rose-400/20'
                          )}
                          placeholder="0.00"
                          value={draftRates[key] ?? ''}
                          onChange={(e) => setRate(key, e.target.value)}
                        />
                      </div>
                      {rateError ? (
                        <p className="text-[10px] font-medium text-rose-600">{rateError}</p>
                      ) : (
                        <p className="text-[10px] text-muted-foreground">
                          {isPerRoom ? 'Same rate for any guest count' : `Rate at ${occ} occupancy`}
                        </p>
                      )}
                    </div>
                  )
                })}
              </div>

              {FloorCeiling}
              {LeadPlanRow}
            </div>
          )}
        </DialogBody>

        <DialogFooter className="flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <PencilLine className="h-3.5 w-3.5 shrink-0 text-amber-500" />
            Staged only — not saved yet. Cancel discards these edits.
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              className="rounded-lg"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="button" className="rounded-lg" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
